import * as React from 'react';

import {Nut_PhoneCaller as PhoneCallerSettings} from 'nutshell-core/settings-constants';

import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';
import {Radio} from 'shells/radio/radio';
import {RadioGroup} from 'shells/radio/radio-group';
import {SavedSettingToast} from 'shells/saved-setting-toast/saved-setting-toast';
import {Body} from 'shells/typography/body';

import type {GetSessionUser_loggedInUser_phoneIntegrations as PhoneIntegration} from 'nutshell-graphql-types';

import {getServiceNoun} from '../../calls/helpers';
import {useGetSetting} from '../../core/use-get-setting';
import {useUpdateSetting} from '../../core/use-update-setting';

const DEFAULT_VALUE = '';

type Props = {
    enabledPhoneIntegrations: PhoneIntegration[];
    clickToCallEnabled: boolean;
    /** When true, show a "saved!" toast. */
    showSavedToast: boolean;
    onError: (arg1: string) => void;
    // Need to set the name because when this component is used in the email
    // onboarding modal on the email sync settings page, the browser gets
    // confused on when buttons to set.
    radioGroupName: string;
};

PhoneIntegrationDefaultProviderSetting.defaultProps = {
    radioGroupName: 'configure default provider',
};

function getTitleForCallOption(
    integrationType: string,
    serviceNoun: string
): string | null | undefined {
    if (integrationType === 'CLICKTOCALL') {
        return `Call with Nutshell ${serviceNoun}`;
    } else if (integrationType === 'AIRCALL') {
        return "Call with Nutshell's embedded Aircall widget";
    }

    return `Call with my ${serviceNoun} app`;
}

function getSubtitleForCallOption(integrationType: string): React.ReactNode | null | undefined {
    const integrationTypeLower = integrationType.toLowerCase();

    if (integrationTypeLower === 'dialpad') {
        return (
            <Body color='light' size='small' isItalic={true}>
                Requires the{' '}
                <Link
                    variant='primary'
                    size='small'
                    newTab={true}
                    href='https://help.dialpad.com/docs/download-dialpad'
                >
                    Dialpad
                </Link>{' '}
                desktop app
            </Body>
        );
    }

    if (integrationTypeLower === 'ringcentral') {
        return (
            <Body color='light' size='small' isItalic={true}>
                Requires the{' '}
                <Link
                    variant='primary'
                    size='small'
                    newTab={true}
                    href='https://www.ringcentral.com/download.html'
                >
                    RingCentral
                </Link>{' '}
                desktop app
            </Body>
        );
    }

    return null;
}

export function PhoneIntegrationDefaultProviderSetting(props: Props) {
    const {showSavedToast, onError, radioGroupName} = props;

    const integrationTypes = props.enabledPhoneIntegrations
        .filter(
            (phoneIntegration) =>
                phoneIntegration.integrationType !== 'CALLRAIL' &&
                phoneIntegration.integrationType !== 'KIXIE'
        ) // currently not supporting callrail or kixie for making calls from Nutshell
        .map((phoneIntegration) => phoneIntegration.integrationType);

    if (props.clickToCallEnabled) {
        integrationTypes.push('CLICKTOCALL');
    }

    const [valueSaved, setValueSaved] = React.useState<string | null | undefined>(null);
    const [isUpdating, setIsUpdating] = React.useState<boolean>(false);

    const savedTimeoutRef = React.useRef();

    const updateSetting = useUpdateSetting();

    const {setting: defaultPhoneProviderSetting, isLoading} = useGetSetting({
        settingName: PhoneCallerSettings.USER_TYPE_SETTINGS_KEY,
    });

    if (
        defaultPhoneProviderSetting &&
        defaultPhoneProviderSetting.value &&
        // @ts-expect-error
        !integrationTypes.includes(defaultPhoneProviderSetting.value.toUpperCase())
    ) {
        // @ts-expect-error
        integrationTypes.push(defaultPhoneProviderSetting.value.toUpperCase());
    }

    const disabled = isLoading || isUpdating;

    let initialValue = DEFAULT_VALUE;

    if (isLoading) {
        // @ts-expect-error
        initialValue = null;
    } else if (defaultPhoneProviderSetting && defaultPhoneProviderSetting.value) {
        initialValue = defaultPhoneProviderSetting.value;
    }

    if (integrationTypes.length === 0) return null;

    return (
        <Stack spacing={8}>
            <Body isBold={true} color='dark'>
                What would you like to do when you click a phone number in Nutshell?
            </Body>
            <RadioGroup
                name={radioGroupName}
                selectedValue={initialValue}
                onChange={(newVal) => {
                    setIsUpdating(true);
                    updateSetting({
                        settingName: PhoneCallerSettings.USER_TYPE_SETTINGS_KEY,
                        value: newVal,
                    })
                        .then((mutationResult) => {
                            setValueSaved(
                                mutationResult &&
                                    mutationResult.data &&
                                    mutationResult.data.updateSetting &&
                                    mutationResult.data.updateSetting.setting.value
                            );
                            setIsUpdating(false);

                            if (showSavedToast) {
                                if (savedTimeoutRef.current) {
                                    clearTimeout(savedTimeoutRef.current);
                                }
                                // @ts-expect-error
                                savedTimeoutRef.current = setTimeout(() => {
                                    setValueSaved(null);
                                }, 1000);
                            }

                            if (typeof NutshellFrontendSettings !== 'undefined') {
                                NutshellFrontendSettings.phonecaller_type = newVal;
                            }
                        })
                        .catch((errMsg) => {
                            onError(errMsg);
                            setIsUpdating(false);
                        });
                }}
            >
                {integrationTypes.map((integrationType) => {
                    const serviceNoun = getServiceNoun(integrationType);
                    const lowerCaseIntegrationType = integrationType.toLowerCase();
                    const title = getTitleForCallOption(integrationType, serviceNoun);
                    const subtitle = getSubtitleForCallOption(integrationType);

                    return (
                        <SavedSettingToast
                            key={lowerCaseIntegrationType}
                            state={
                                showSavedToast && valueSaved === lowerCaseIntegrationType
                                    ? 'complete'
                                    : 'initial'
                            }
                        >
                            <Radio
                                value={lowerCaseIntegrationType}
                                disabled={disabled}
                                shouldTopAlignInput={true}
                            >
                                <Stack spacing={4}>
                                    <Body>{title}</Body>
                                    {subtitle}
                                </Stack>
                            </Radio>
                        </SavedSettingToast>
                    );
                })}
                <SavedSettingToast
                    state={showSavedToast && valueSaved === DEFAULT_VALUE ? 'complete' : 'initial'}
                >
                    <Radio value={DEFAULT_VALUE} disabled={disabled}>
                        <Body>Nothing</Body>
                    </Radio>
                </SavedSettingToast>
            </RadioGroup>
        </Stack>
    );
}
